import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Subscription } from '@adornis/baseql/decorators.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { Partner } from '../master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { ContextPrefix } from '../permissions.js';
import { SemanticRelationship } from './SemanticRelationship.js';

/**
 * @entity PartnerRelationship
 */
@Entity()
export class PartnerRelationship extends SemanticRelationship {
  public static override _class = 'PartnerRelationship';

  @DataLoadedField(() => ChancenwerkUser, ({ subjectID }) => subjectID)
  public override subject: Maybe<ChancenwerkUser>;

  @DataLoadedField(() => Partner, ({ objectID }) => objectID)
  public override object: Maybe<Partner>;

  @Field(() => String)
  public position: Maybe<string>;

  public override getContext() {
    return this.objectID === GLOBAL_CONTEXT ? GLOBAL_CONTEXT : ContextPrefix.SCHOOL + this.objectID;
  }

  // TODO abstract this into superclass
  @Subscription(() => [PartnerRelationship])
  static subscribePartnerRelationsForPartner(@Arg('partnerID', () => String) partnerID: string) {
    return (gqlFields: BaseQLSelectionSet<PartnerRelationship>) => {
      const selector = { _class: this._class, objectID: partnerID };
      const handle = getCollectionHandle<PartnerRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as PartnerRelationship[]);
        }),
      );
    };
  }

  // TODO abstract this into superclass
  @Subscription(() => [PartnerRelationship])
  static subscribePartnerRelationsForUser(@Arg('userID', () => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<PartnerRelationship>) => {
      const selector = { _class: this._class, subjectID: userID };
      const handle = getCollectionHandle<PartnerRelationship>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as PartnerRelationship[]);
        }),
      );
    };
  }
}
